import type { ShopSettingsDTO } from "@/modules/chat-tools/domain/contracts";
import { DEFAULT_SHOP_DISPLAY_NAME } from "@/modules/chat-tools/domain/default-template";
import { getDefaultShopSettings } from "@/modules/chat-tools/domain/repo/chat-tools";
import { formatOrderSummaryQty } from "@/modules/chat-tools/domain/build-order-summary-card";
import { formatLak } from "@/shared/lib/currency";
import { formatDateLocal } from "@/shared/lib/date-time";
import type { CustomJobDetailDTO, CustomJobItemDTO } from "./types";

const LINE_TYPE_LABEL: Record<string, string> = {
  labor: "ຄ່າແຮງ",
  other: "ອື່ນໆ",
};

export type CustomJobSummaryVariantLine = {
  variantName: string;
  quantity: number;
  unitLabel: string;
  amountLabel: string;
};

export type CustomJobSummaryProductGroup = {
  productName: string;
  variants: CustomJobSummaryVariantLine[];
};

export type CustomJobSummaryCardViewModel = {
  shopName: string;
  jobNumber: string;
  customerName: string;
  phone: string | null;
  dueDateLabel: string | null;
  note: string | null;
  itemGroups: CustomJobSummaryProductGroup[];
  subtotal: string;
  discount: string | null;
  total: string;
  paymentLabel: string;
};

function resolveMaterialUnitLabel(_item: CustomJobItemDTO): string {
  return "ຊິ້ນ";
}

export function groupCustomJobSummaryItems(
  items: CustomJobItemDTO[],
): CustomJobSummaryProductGroup[] {
  const groups: CustomJobSummaryProductGroup[] = [];
  const indexByKey = new Map<string, number>();

  for (const item of items) {
    if (item.lineType === "material") {
      const productName = item.productName?.trim() || item.description;
      const variantName = item.variantName?.trim() || item.description;
      let idx = indexByKey.get(productName);
      if (idx === undefined) {
        idx = groups.length;
        indexByKey.set(productName, idx);
        groups.push({ productName, variants: [] });
      }
      groups[idx]?.variants.push({
        variantName,
        quantity: item.quantity,
        unitLabel: resolveMaterialUnitLabel(item),
        amountLabel: formatLak(item.lineTotal),
      });
      continue;
    }

    groups.push({
      productName: item.description,
      variants: [
        {
          variantName: LINE_TYPE_LABEL[item.lineType] ?? item.lineType,
          quantity: item.quantity,
          unitLabel: "ລາຍການ",
          amountLabel: formatLak(item.lineTotal),
        },
      ],
    });
  }

  return groups;
}

export function buildCustomJobSummaryCardViewModel(
  job: CustomJobDetailDTO,
  settings?: ShopSettingsDTO,
): CustomJobSummaryCardViewModel {
  const shop = settings ?? getDefaultShopSettings();
  const discountAmount = Number(job.discountAmount);

  return {
    shopName: shop.shopDisplayName.trim() || DEFAULT_SHOP_DISPLAY_NAME,
    jobNumber: job.jobNumber,
    customerName: job.customerName,
    phone: job.customerPhone?.trim() || null,
    dueDateLabel: job.dueDate ? formatDateLocal(job.dueDate) : null,
    note: job.note?.trim() || null,
    itemGroups: groupCustomJobSummaryItems(job.items),
    subtotal: formatLak(job.subtotal),
    discount:
      discountAmount > 0 ? `-${formatLak(job.discountAmount)}` : null,
    total: formatLak(job.total),
    paymentLabel: "ຈ່າຍເມື່ອມາຮັບ",
  };
}

export { formatOrderSummaryQty };
